import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import DropshipWorkspaceShell from "../../components/DropshipWorkspaceShell";
import { ConnectStoreTab } from "./dropship/setup";
import {
  AnalyticsTab,
  CatalogSyncTab,
  FulfillmentCenterTab,
  InvoicesTab,
  ClaimsOrReturnsSeller,
} from "./dropship/tabs";
export { TermsEditor } from "./dropship/tabs";
import { Button } from "../../components/ui/button";
import {
  Package, Truck, BarChart3, Receipt, Plug, AlertTriangle,
  RefreshCw, LayoutGrid,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// SellerDropship — full dropship OS for the supplier side.
// 8 tabs map 1:1 to the user's mental model of "how dropship works":
//   1. Connect store      — plug a buyer's Shopify
//   2. Catalog sync       — push our products into their store
//   3. Automation mode    — Full Auto vs Daily Confirm
//   4. Terms              — mutual agreement on returns / damage / delays
//   5. Daily Orders       — today's incoming orders w/ confirmation deadline
//   6. Tracking           — movement monitor with stalled / no-movement alerts
//   7. Auto-Invoices      — daily consolidated invoices with deductions
//   8. Analytics          — revenue / AOV / fulfillment rate / top customers
//
// AI-curated catalog push and multi-platform (Woo / Amazon / etc.) are
// surfaced as "Coming soon" cards rather than empty tabs — preserves the
// vision without empty calories.
// ─────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "connect",   label: "Setup",    icon: Plug },
  { id: "catalog",   label: "Catalog Sync",   icon: LayoutGrid },
  { id: "fulfillment", label: "Fulfillment",  icon: Truck, badge: true },
  { id: "invoices",  label: "Auto-Invoices",  icon: Receipt },
  { id: "claims",    label: "Claims",         icon: AlertTriangle },
  { id: "analytics", label: "Analytics",      icon: BarChart3 },
];

export default function SellerDropship() {
  const [search, setSearch] = useSearchParams();
  // Legacy redirects:
  //  · `?tab=terms` → Store Setup (unified)
  //  · `?tab=daily` → Fulfillment (Daily Orders merged into Fulfillment >
  //    Shipments > "All orders" view in Feb 2026).
  const rawTab = search.get("tab") || "fulfillment";
  const tab = rawTab === "terms" ? "connect"
            : rawTab === "daily" ? "fulfillment"
            : rawTab;
  // Rewrite the URL bar so legacy bookmarks (?tab=daily, ?tab=terms) land
  // honestly on the current tab — makes direct bookmarking of ?tab=fulfillment
  // possible and keeps history clean.
  useEffect(() => {
    if (rawTab !== tab) {
      const next = new URLSearchParams(search);
      next.set("tab", tab);
      setSearch(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab, tab]);
  const setTab = (t) => {
    const next = new URLSearchParams(search);
    next.set("tab", t);
    setSearch(next, { replace: true });
  };

  const [orders, setOrders] = useState([]);
  const [movement, setMovement] = useState({ moving: 0, stalled: 0, no_movement: 0 });
  const [pendingClaims, setPendingClaims] = useState(0);

  const load = async () => {
    try {
      const [ord, m, claims] = await Promise.all([
        api.get("/dropship/orders"),
        api.post("/dropship/movement-monitor/refresh"),
        api.get("/dropship/claims"),
      ]);
      setOrders(ord.data);
      setMovement(m.data || {});
      setPendingClaims((claims.data || []).filter(c => c.status === "pending").length);
    } catch { /* best-effort load */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => ({
    // Fulfillment badge · merges the legacy "daily" count (forwarded +
    // movement red-flags) so sellers see the same urgency they used to
    // see on the standalone Daily Orders tab.
    fulfillment: orders.filter(o => o.status === "forwarded").length
                 + (movement.stalled || 0) + (movement.no_movement || 0),
    claims:      pendingClaims,
  }), [orders, movement, pendingClaims]);

  return (
    <DropshipWorkspaceShell
      role="seller"
      pageHeader={
        <PageHeader
          title="Dropship Automation"
          subtitle="The channel-agnostic supplier-side dropship OS"
          action={
            // Hide Refresh on the Fulfillment tab (Feb 2026 · Iter 203).
            // The Fulfillment Center header already hosts a "Rescan" button
            // that (a) polls carriers for new tracking events AND (b) calls
            // load() to re-sync our DB — so Refresh is redundant there.
            // Kept on all other tabs where no Rescan exists.
            tab === "fulfillment" ? null : (
              <Button
                onClick={load}
                variant="outline"
                data-testid="dropship-refresh"
                className="rounded-full h-9 px-4"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
              </Button>
            )
          }
        />
      }
      preTabsSlot={null}
    >
      <div className="px-8 pb-8 pt-3">
        {/* ── Tab strip ─────────────────────────────────────────────
             Top-level tabs. Iter 241 · Standardized to platform typography
             scale (h-11 · text-sm · font-medium · w-4 icon · border-b-2)
             so the dropship shell matches every other portal + app.
             Each tab retains its accent color for section identifiability
             (Stripe/Linear/Notion pattern) — only sizing changes. */}
        <div className="flex gap-0 items-end overflow-x-auto border-b border-border -mx-8 px-8 sticky top-0 bg-background/95 backdrop-blur z-10" data-testid="dropship-tabs">
          {TABS.map(t => {
            const active = tab === t.id;
            const c = t.badge ? counts[t.id] : 0;
            // Per-tab accent colors — mirrors how modern SaaS dashboards
            // color-code each section (Stripe, Linear, Notion pattern).
            const accents = {
              connect:     { border: "border-emerald-600", text: "text-emerald-600", bg: "bg-emerald-600" },
              catalog:     { border: "border-blue-600",    text: "text-blue-600",    bg: "bg-blue-600" },
              fulfillment: { border: "border-indigo-600",  text: "text-indigo-600",  bg: "bg-indigo-600" },
              invoices:    { border: "border-violet-600",  text: "text-violet-600",  bg: "bg-violet-600" },
              claims:      { border: "border-amber-600",   text: "text-amber-600",   bg: "bg-amber-600" },
              analytics:   { border: "border-rose-600",    text: "text-rose-600",    bg: "bg-rose-600" },
            };
            const ac = accents[t.id] || { border: "border-foreground", text: "text-foreground", bg: "bg-foreground" };
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`dropship-tab-${t.id}`}
                className={`relative h-11 px-4 text-sm font-medium border-b-2 -mb-[1px] transition-all inline-flex items-center gap-2 whitespace-nowrap group ${
                  active
                    ? `${ac.border} text-slate-900`
                    : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-secondary/40"
                }`}
              >
                <t.icon className={`w-4 h-4 transition-colors ${active ? ac.text : "text-slate-400 group-hover:text-slate-700"}`}/>
                <span>{t.label}</span>
                {c > 0 && (
                  <span className={`ml-0.5 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-semibold tabular-nums ${
                    t.id === "fulfillment" && (movement.stalled || movement.no_movement)
                      ? "bg-rose-100 text-rose-700"
                      : active
                        ? `${ac.bg} text-white`
                        : "bg-slate-200 text-slate-700"
                  }`}>{c > 99 ? "99+" : c}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="pt-6">
          {tab === "connect"   && <ConnectStoreTab onChanged={load}/>}
          {tab === "catalog"   && <CatalogSyncTab/>}
          {tab === "fulfillment" && <FulfillmentCenterTab
                                        orders={orders}
                                        reload={load}
                                        movement={movement}
                                        onRescan={async () => {
                                          const r = await api.post("/dropship/movement-monitor/refresh");
                                          setMovement(r.data || {});
                                          load();
                                        }}
                                      />}
          {tab === "invoices"  && <InvoicesTab reload={load}/>}
          {tab === "claims"    && <ClaimsOrReturnsSeller orders={orders} reload={load}/>}
          {tab === "analytics" && <AnalyticsTab/>}
        </div>
      </div>
    </DropshipWorkspaceShell>
  );
}


